import { useDeleteProvider, useGetProvider } from '@/api/voucher-providers/voucher-providers';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ImageUrlsField } from '@/components/ui/image-urls-field';
import { Form } from '@/components/ui/form';
import { VoucherProviderEditForm } from '@/components/voucher-providers/VoucherProviderEditForm';
import type { VoucherProviderFormData } from '@/components/voucher-providers/VoucherProviderForm';
import { ArrowLeft, Eye, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/api-error';
import { ProviderCategory } from '@/api/model';

export const VoucherProviderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    data: provider,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetProvider(id ?? '', {
    query: {
      enabled: !!id,
    },
  });

  const deleteProviderMutation = useDeleteProvider();

  // All hooks must be declared before any early returns
  const [previewData, setPreviewData] = useState<VoucherProviderFormData>({
    name: provider?.name || '',
    email: provider?.email || '',
    address: provider?.address || '',
    phone: provider?.phone || '',
    short_description: provider?.short_description || '',
    logo_urls: provider?.logo_url ? [provider.logo_url] : [],
    category: provider?.category || ProviderCategory.SERVICES,
  });
  const [formInstance, setFormInstance] = useState<UseFormReturn<VoucherProviderFormData> | null>(null);

  // Update preview data when provider loads
  React.useEffect(() => {
    if (provider) {
      setPreviewData({
        name: provider.name,
        email: provider.email,
        address: provider.address,
        phone: provider.phone || '',
        short_description: provider.short_description || '',
        logo_urls: provider.logo_url ? [provider.logo_url] : [],
        category: provider.category,
      });
    }
  }, [provider]);

  if (isLoading) {
    return (
      <div className='container mx-auto p-6'>
        <div className='text-center'>Anbieterdetails werden geladen...</div>
      </div>
    );
  }

  if (isError || !provider) {
    return (
      <div className='container mx-auto p-6'>
        <div className='text-center text-red-500'>
          {error instanceof Error ? `Fehler beim Laden des Anbieters: ${error.message}` : 'Anbieter nicht gefunden'}
        </div>
        <div className='flex justify-center mt-4'>
          <Button onClick={() => navigate('/admin/providers')}>Zurück zu Anbietern</Button>
        </div>
      </div>
    );
  }

  const handleEditSuccess = () => {
    refetch();
  };

  const handleCancelEdit = () => {
    navigate('/admin/providers');
  };

  const handleDeleteConfirm = () => {
    if (provider?.id) {
      deleteProviderMutation
        .mutateAsync({ providerId: provider.id })
        .then(() => {
          toast.success('Anbieter wurde erfolgreich gelöscht');
          navigate('/admin/providers');
        })
        .catch(error => {
          toast.error(getApiErrorMessage(error, 'Fehler beim Löschen des Anbieters'));
        });
    }
  };

  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getCategoryLabel = (category: ProviderCategory): string => {
    const labels: Record<ProviderCategory, string> = {
      [ProviderCategory.GASTRONOMIE]: 'Gastronomie',
      [ProviderCategory.ERLEBEN]: 'Erleben',
      [ProviderCategory.SHOPPING]: 'Shopping',
      [ProviderCategory.SERVICES]: 'Services',
    };
    return labels[category] || category;
  };

  return (
    <div className='space-y-6'>
      {/* Page Header */}
      <div className='flex flex-col gap-2'>
        <div className='flex items-center gap-4'>
          <Link to='/admin/providers' className='flex items-center gap-2'>
            <ArrowLeft className='h-6 w-6 text-swd-tuerkis-dark' />
          </Link>
          <h1 className='text-3xl leading-9 font-semibold text-swd-tuerkis-darker'>Partner bearbeiten</h1>
          <div className='flex gap-2 ml-auto'>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant='outline'
                  className='h-[42px] rounded-lg border-neutral-hellgrau bg-white text-neutral-dunkelgrau text-sm font-medium tracking-wide px-4 hover:bg-neutral-hellgrau/50'
                  disabled={deleteProviderMutation.isPending}
                >
                  <Trash2 className='h-4 w-4 mr-2' />
                  Löschen
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Partner löschen</AlertDialogTitle>
                  <AlertDialogDescription>
                    Sind Sie sicher, dass Sie den Partner "{provider?.name}" wirklich löschen möchten? Diese Aktion kann
                    nicht rückgängig gemacht werden.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDeleteConfirm}
                    className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
                  >
                    {deleteProviderMutation.isPending ? 'Wird gelöscht...' : 'Löschen'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
        <p className='text-base leading-6 font-medium tracking-wide text-neutral-grau'>
          Aktualisieren Sie die Partnerinformationen
        </p>
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
        {/* Form Section */}
        <div className='lg:col-span-2'>
          <VoucherProviderEditForm
            provider={provider}
            onSuccess={handleEditSuccess}
            onCancel={handleCancelEdit}
            onFormDataChange={setPreviewData}
            hideLogoField
            onFormInstance={setFormInstance}
          />
        </div>

        {/* Preview Section */}
        <div className='lg:sticky lg:top-6 lg:self-start lg:col-span-1 space-y-6'>
          <Card className='border border-neutral-hellgrau rounded-lg'>
            <CardHeader>
              <div className='flex items-center gap-2'>
                <Eye className='h-5 w-5 text-swd-tuerkis-darker' />
                <CardTitle className='text-neutral-dunkelgrau text-xl font-bold leading-7'>Vorschau</CardTitle>
              </div>
            </CardHeader>
            <CardContent className='space-y-4'>
              <div className='flex flex-col items-center gap-4'>
                <Avatar className='h-24 w-24'>
                  <AvatarImage
                    src={
                      previewData.logo_urls && previewData.logo_urls.length > 0
                        ? previewData.logo_urls[0]
                        : provider.logo_url || undefined
                    }
                    alt={previewData.name || provider.name}
                  />
                  <AvatarFallback className='text-2xl font-semibold'>
                    {getInitials(previewData.name || provider.name)}
                  </AvatarFallback>
                </Avatar>
                <div className='text-center'>
                  <h3 className='font-semibold text-lg'>{previewData.name || provider.name}</h3>
                  <p className='text-sm text-neutral-mittelgrau'>{getCategoryLabel(previewData.category)}</p>
                  {previewData.short_description && (
                    <p className='text-sm text-neutral-grau mt-2'>{previewData.short_description}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {formInstance && (
            <Form {...formInstance}>
              <ImageUrlsField control={formInstance.control} name='logo_urls' label='Logo' maxImages={1} />
            </Form>
          )}
        </div>
      </div>
    </div>
  );
};
